import type { DrawContext } from './draw';
import type { View } from './project';
import type { Random } from './rng';

/*
 * THE STAR FIELD — the layer their own comment names as the biggest bill in the engine.
 *
 * Ported from `reference/universe-v8/engine.js` — the field is built at 516–521 and drawn at
 * 1464–1511. Their comment on the draw:
 *
 *   "Batched by brightness. One fillStyle string and one fill() per band instead of per star: at
 *    11,000 stars the per-star fillStyle + toFixed + fillRect was the single largest cost in the
 *    static layer, and the static layer redraws every frame while the camera moves."
 *
 * THAT IS THE WHOLE LESSON OF THIS FILE, and it is the one our old renderer needed most. Eleven
 * thousand stars is eleven thousand of everything if you draw them one at a time — and the expensive
 * part is not the pixels, it is the STATE CHANGES: a `fillStyle` assignment is a string parse, and
 * `toFixed` allocates. Sorting the stars into five brightness bands turns eleven thousand style
 * changes into five, and eleven thousand fills into five. The pixels were never the problem.
 *
 * The same trick appears again in the edge layer as one stroke per width bucket. It is the single
 * idea to carry into every layer still to come.
 *
 * THE STARS SPIN AT HALF THE WORLD'S RATE. That is the parallax: they use a spin angle of `spinA/2`
 * while the camera offset `pcx`/`pcy` is the full-spin one, so the sky slides against the field
 * rather than turning with it. It looks like a mistake and it is the effect.
 *
 * A SECOND FIELD, `nearDust`, drifts in screen space in `drawFrame` (1780–1796). Not this layer.
 */

/** Where the stars are and how many. Both world dimensions, so both arguments. */
export interface StarWorld {
  /**
   * How far out the field reaches, in world units.
   *
   * Theirs is 3,400 against a rim of 12,320 — their comment says the field "only spans r=3,400 of a
   * 10,441 world", which is what makes it a dense blob behind the sky when pulled right back.
   */
  extent: number;
  /**
   * How many stars to generate.
   *
   * Theirs is 16,000, of which only the first 11,000 are ever drawn. It is a DENSITY over `extent`,
   * so it moves with the extent rather than being a look choice on its own.
   */
  count: number;
  /** The world's vertical scale. */
  squash: number;
}

/**
 * THE THREE ZOOMS THE LAYER CHANGES AT — arguments, because a zoom is pixels per world unit.
 *
 * A world ten times the size is read at a tenth the zoom, so a copied threshold lands in the wrong
 * place. This is the mistake that cost the first port twenty-nine screens of overdraw a frame with
 * an absolute wash fade; the same trap, three more times.
 */
export interface StarZooms {
  /** Below this, draw far fewer of them: pulled right back they are a blob behind the sky. */
  thin: number;
  /** Below this they are dimmer, because the field is competing with the whole map. */
  dim: number;
  /** Above this they are brighter, because by then the map is sparse and the sky can carry it. */
  bright: number;
}

/**
 * THEIRS, as the engine states them: engine.js:1479 and 1497 read
 * `(0.04+bm*0.19)*(z<0.3?0.7:(z>0.85?1.55:1))` — dim below 0.3, brighter above 0.85 — and 0.12 is
 * where the field thins.
 *
 * Exported so the wiring and the test read ONE copy. They did not: the test held 0.85 and the
 * scene wired 0.9, which is their label weight-gate from line 1693 and not a star threshold at all.
 */
export const THEIR_STAR_ZOOMS: StarZooms = { thin: 0.12, dim: 0.3, bright: 0.85 };

/** How much of the field a caller wants at all — the reader's own preference, clamped. */
const DENSITY_MIN = 0.15;
const DENSITY_MAX = 1.6;

/** The most stars any frame draws, before the density and the zoom are applied. */
const DRAW_BASE = 11_000;

/** How much of that survives below the `thin` zoom. */
const THIN_SHARE = 0.38;

/** Nobody counts stars mid-flight, so a streaking frame draws far fewer. */
const STREAK_CAP = 3600;

/** Below this speed there is no streak at all; above it the streak grows to 1. */
const STREAK_FROM = 60;
const STREAK_RATE = 0.008;
/**
 * And below this much streak the layer draws dots rather than lines.
 *
 * Never the binding gate as they ship it: the slowest streaking speed is 60, which gives 0.48, so
 * `streak > 0.12` is true whenever `streak` is non-zero at all. Kept because it is theirs and
 * because a caller who moved `STREAK_FROM` would need it.
 */
const STREAK_MIN = 0.12;

/** How long a streak is, as a share of the distance still to travel. */
const STREAK_LENGTH = 0.06;

/** How many brightness bands, still and streaking. Fewer while moving: nobody is looking. */
const BANDS_STILL = 5;
const BANDS_STREAK = 3;

/** A band's alpha, from its middle brightness. Look. */
const ALPHA_FLOOR = 0.04;
const ALPHA_RANGE = 0.19;
/** What the three zoom bands do to it. Look. */
const DIM_SHARE = 0.7;
const BRIGHT_SHARE = 1.55;
/** A streak is fainter than a dot of the same brightness. */
const STREAK_ALPHA = 0.7;

/** How wide a star is on screen, by band. Look. */
const SIZE_BIG = 1.2;
const SIZE_MID = 0.9;
const SIZE_SMALL = 0.7;
const BIG_ABOVE = 0.93;
const MID_ABOVE = 0.55;

/** How wide a streak is drawn. Look. */
const STREAK_WIDTH_FLOOR = 0.7;
const STREAK_WIDTH_RANGE = 0.6;

/** How far off screen a star is still drawn. Screen pixels, so look. */
const STAR_MARGIN = 40;

/** How much of the world's spin the stars take. Half — which is what makes the parallax. */
const STAR_SPIN_SHARE = 0.5;

/** Three numbers a star: where it is, and how bright. Flat, because eleven thousand of them. */
export const STAR_STRIDE = 3;

/**
 * Build the field.
 *
 * `pow(r, 0.55)` biases outward, so an even scatter does not crowd the middle — the same correction
 * the dust makes, and for the same reason: a ring's area grows with its radius.
 */
export function buildStars(world: StarWorld, random: Random): Float32Array {
  const stars = new Float32Array(world.count * STAR_STRIDE);
  for (let i = 0; i < world.count; i++) {
    const angle = random() * Math.PI * 2;
    const radius = Math.pow(random(), 0.55) * world.extent;
    stars[i * STAR_STRIDE] = Math.cos(angle) * radius;
    stars[i * STAR_STRIDE + 1] = Math.sin(angle) * radius * world.squash;
    stars[i * STAR_STRIDE + 2] = random();
  }
  return stars;
}

/**
 * How many of them this frame draws.
 *
 * ONE DEVIATION, STATED: theirs can ask for more stars than the field holds. At their maximum
 * density of 1.6 the count is 17,600 against a field of 16,000, and the loop then reads past the
 * end, gets `undefined`, fails both brightness comparisons (neither `<` nor `>=` is true of
 * `undefined`) and draws a rectangle at `NaN, NaN`. That is not a look anybody chose, so the count
 * is clamped to what the field actually holds. Every camera below a density of about 1.45 is
 * unaffected, and the differential tests cover the range where theirs is well defined.
 */
export function starDrawCount(stars: Float32Array, density: number, zoom: number, zooms: StarZooms): number {
  const wanted = Math.floor(
    DRAW_BASE * Math.max(DENSITY_MIN, Math.min(DENSITY_MAX, density)) * (zoom < zooms.thin ? THIN_SHARE : 1),
  );
  return Math.min(wanted, Math.floor(stars.length / STAR_STRIDE));
}

/** How much the camera's speed smears the stars. Zero until it is worth the different code path. */
export function starStreak(speed: number): number {
  return speed > STREAK_FROM ? Math.min(1, speed * STREAK_RATE) : 0;
}

/** Whether this frame draws lines rather than dots. */
export function streaking(streak: number): boolean {
  return streak > STREAK_MIN;
}

/**
 * How far a streak reaches, in screen pixels.
 *
 * Backwards along the way the camera is STILL GOING — the offset from where it is to where it is
 * headed — so the smear trails the motion rather than leading it.
 */
export function starDrift(view: View, target: { x: number; y: number }, streak: number): { dx: number; dy: number } {
  const z = view.cam.z;
  return {
    dx: -(target.x - view.cam.x) * z * STREAK_LENGTH * streak,
    dy: -(target.y - view.cam.y) * z * STREAK_LENGTH * streak,
  };
}

/** A band's alpha at this zoom. */
function bandAlpha(middle: number, zoom: number, zooms: StarZooms): number {
  const scale = zoom < zooms.dim ? DIM_SHARE : zoom > zooms.bright ? BRIGHT_SHARE : 1;
  return (ALPHA_FLOOR + middle * ALPHA_RANGE) * scale;
}

/**
 * How wide a star of this brightness is drawn.
 *
 * THE TOP SIZE IS UNREACHABLE AS THEY SHIP IT, and that is worth writing down rather than tidying.
 * A band's middle is `(2·band + 1) / (2·BANDS)`, so five bands give 0.1, 0.3, 0.5, 0.7 and 0.9 —
 * and `0.9 > 0.93` is false. Nothing in their still path is ever drawn at 1.2 pixels. Found by
 * mutating the constant and watching the tests pass, which is the same way the far band turned out
 * to be composed entirely of local ties. Ported unchanged, because a sixth band or a caller with a
 * different split would reach it, and because the alternative is inventing a size they never drew.
 */
export function starSize(middle: number): number {
  return middle > BIG_ABOVE ? SIZE_BIG : middle > MID_ABOVE ? SIZE_MID : SIZE_SMALL;
}

/** The middle brightness of one band of N. What decides a star's size and its alpha. */
export function bandMiddle(band: number, bands: number): number {
  return (band / bands + (band + 1) / bands) / 2;
}

/**
 * Draw the field, batched by brightness.
 *
 * `spinAngle` is passed raw rather than taken from the view, because the stars need HALF of it and
 * the view has already resolved the full angle into its cosine and sine.
 */
export function drawStars(
  ctx: DrawContext,
  view: View,
  spinAngle: number,
  stars: Float32Array,
  count: number,
  zooms: StarZooms,
  streak: number,
  drift: { dx: number; dy: number },
): void {
  const half = spinAngle * STAR_SPIN_SHARE;
  const svc = Math.cos(half);
  const svs = Math.sin(half);
  const z = view.cam.z;
  const sq = view.squash;
  const W = view.view.width;
  const H = view.view.height;
  /* Set once, whichever path runs — a streak with a butt cap reads as a scratch rather than a smear.
     Theirs sets it outside the branch too, so it survives into whatever is drawn next. */
  ctx.lineCap = 'round';

  const bands = streaking(streak) ? BANDS_STREAK : BANDS_STILL;
  for (let band = 0; band < bands; band++) {
    const b0 = band / bands;
    const b1 = (band + 1) / bands;
    const middle = bandMiddle(band, bands);
    const alpha = bandAlpha(middle, z, zooms);
    if (streaking(streak)) {
      ctx.strokeStyle = `rgba(190,206,238,${(alpha * STREAK_ALPHA).toFixed(3)})`;
      ctx.lineWidth = STREAK_WIDTH_FLOOR + middle * STREAK_WIDTH_RANGE;
    } else {
      ctx.fillStyle = `rgba(204,201,186,${alpha.toFixed(3)})`;
    }
    const size = starSize(middle);
    ctx.beginPath();
    let any = false;
    for (let i = 0; i < count; i++) {
      const b = stars[i * STAR_STRIDE + 2] ?? 0;
      if (b < b0 || b >= b1) continue;
      const wx = stars[i * STAR_STRIDE] ?? 0;
      const wy = (stars[i * STAR_STRIDE + 1] ?? 0) / sq;
      /* Not `toScreen`: the half-spin is the point, and the camera offset is the full-spin one. */
      const sx = (wx * svc - wy * svs - view.pcx) * z + W / 2;
      const sy = ((wx * svs + wy * svc) * sq * view.vt - view.pcy) * z + H / 2;
      if (sx < -STAR_MARGIN || sx > W + STAR_MARGIN || sy < -STAR_MARGIN || sy > H + STAR_MARGIN) continue;
      if (streaking(streak)) {
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + drift.dx, sy + drift.dy);
      } else {
        ctx.rect(sx, sy, size, size);
      }
      any = true;
    }
    /* ONE call for the whole band. The saving this file exists for. */
    if (any) {
      if (streaking(streak)) ctx.stroke();
      else ctx.fill();
    }
  }
}

/** The count a streaking frame is held to. Exposed because the caller applies it before drawing. */
export function streakCount(count: number): number {
  return Math.min(count, STREAK_CAP);
}
